import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Crown } from "lucide-react";
import AppLayout from "../components/AppLayout";
import LeadForm from "../components/LeadForm";
import LeadOutreachSection from "../components/outreach/LeadOutreachSection";
import api, { formatError } from "../lib/api";
import { STAGE_STYLES } from "../lib/constants";
import { toast } from "sonner";

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/leads");
      const found = data.find((l) => l.id === id);
      if (found) setLead(found);
      else setNotFound(true);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to load lead");
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (lead?.company_name) document.title = `${lead.company_name} · King of IT CRM`;
    return () => { document.title = "King of IT CRM Portal"; };
  }, [lead]);

  const st = lead ? (STAGE_STYLES[lead.status] || STAGE_STYLES.New) : null;

  return (
    <AppLayout>
      <div data-testid="lead-detail-page" className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto animate-fade-in">
        <Link
          to="/app"
          data-testid="lead-detail-back"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-400 transition-colors mb-5"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Pipeline
        </Link>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading lead…
          </div>
        ) : notFound ? (
          <div className="rounded-xl border border-slate-800 bg-[#131C31] p-10 text-center">
            <Crown className="w-8 h-8 text-amber-500/60 mx-auto mb-3" />
            <p className="text-white font-semibold">Lead not found</p>
            <p className="text-slate-400 text-sm mt-1">It may have been deleted or the link is invalid.</p>
            <Link to="/app" className="btn-primary rounded-lg py-2 px-4 text-sm inline-flex items-center gap-2 mt-4">
              <ArrowLeft className="w-4 h-4" /> Back to Pipeline
            </Link>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-800 bg-[#131C31] p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-4 border-b border-slate-800">
              <div className="min-w-0">
                <h1 className="font-display font-black text-2xl tracking-tight truncate">{lead.company_name}</h1>
                {lead.contact_name && (
                  <p className="text-slate-400 text-sm mt-0.5">{lead.contact_name}{lead.title ? ` · ${lead.title}` : ""}</p>
                )}
              </div>
              {st && (
                <span data-testid="lead-detail-status" className="text-xs px-3 py-1 rounded-full border shrink-0" style={{ color: st.text, borderColor: st.border, background: st.bg }}>
                  {lead.status}
                </span>
              )}
            </div>

            <LeadForm
              lead={lead}
              mode="page"
              onSaved={(data) => setLead(data)}
              onDeleted={() => navigate("/app")}
              onRefresh={(data) => { if (data) setLead(data); else load(); }}
              onCancel={() => navigate("/app")}
            />
          </div>
        )}
        {lead && <LeadOutreachSection lead={lead} onLeadChange={setLead} />}
      </div>
    </AppLayout>
  );
}
